/**
 * D4 review 9 (browser e2e D4-01) - the group gate's refusals end with their
 * machine code in brackets (` [group_gate:<what>]`, migration 1791302000000),
 * because a refusal raised at statement time reaches a caller without its
 * hint. A screen that shows a GraphQL error's message must never show that
 * tag: this link takes it off every error of a result and keeps it as
 * `extensions.gateHint` (lib/groups/gate-refusal.ts maps it to a code).
 * Pure apart from the link itself - safe in the browser.
 */

import { ApolloLink } from '@apollo/client'
import { map } from 'rxjs'
import { splitGateMarker } from '@/lib/hasura-pg-error'

type ErrorLike = { message: string; extensions?: Record<string, unknown> }

/** The result with every gate marker taken off its errors' messages (kept as `extensions.gateHint`). */
export function stripGateMarkers<R extends { errors?: ReadonlyArray<ErrorLike> }>(result: R): R {
  if (!result.errors?.length || !result.errors.some((e) => splitGateMarker(e.message).hint)) return result
  return {
    ...result,
    errors: result.errors.map((e) => {
      const split = splitGateMarker(e.message)
      return split.hint
        ? { ...e, message: split.message ?? e.message, extensions: { ...(e.extensions ?? {}), gateHint: split.hint, ...(split.params ? { gateParams: split.params } : {}) } }
        : e
    }),
  }
}

/** Apollo link: sits outside the error link, so every reader above it sees the plain sentence. */
export const gateMarkerLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(map((result) => stripGateMarkers(result as { errors?: ReadonlyArray<ErrorLike> }) as typeof result))
)
